import React, { FC, useEffect, useRef, useState } from 'react';
import firebase from 'firebase/app';
import { ui, button } from 'util/ui';
import useOrgSettings from 'hooks/useOrgSettings';
import useAuth from 'hooks/useAuth';
import { RELEASE, RELEASE_DATE, ENVIRONMENT, COMMIT, BUILT_AT } from 'version';
import CloseButton from 'components/shell/CloseButton';

/**
 * UI refresh footer (Aaron, 5 Nov 2026):
 * - left: the company hosting this database (from Settings, so each customer sees their own name);
 * - centre: Powered by Archy, Privacy, Terms, System status, version "2026.45.1 · 5 Nov 2026";
 * - right: the Help button, which opens the customer's own help contact.
 * No © or ABN. Privacy and Terms pages come later, once the platform is complete.
 */

/** The footer floats at the bottom of the window, always visible (Aaron, 5 Nov 2026). */
export const FOOTER_HEIGHT = 56;

const REGION =
  process.env.REACT_APP_PROJECTID === 'cobox-cloud-database'
    ? 'Tokyo (asia-northeast1)'
    : process.env.REACT_APP_PROJECTID === 'cobox-cloud-database-dev'
    ? 'Sydney (australia-southeast1)'
    : 'Unknown';

const Popover: FC<{ title: string; onClose(): void; id: string }> = ({ title, onClose, id, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // The footer buttons toggle the pop-up themselves.
      if (target.closest && target.closest('[data-popover-toggle]')) return;
      if (ref.current && !ref.current.contains(target)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-labelledby={id}
      style={{
        position: 'fixed',
        right: 16,
        bottom: FOOTER_HEIGHT + 12,
        width: 'min(420px, calc(100vw - 32px))',
        background: ui.surface,
        borderRadius: 16,
        boxShadow: '0 18px 44px rgba(0,0,0,0.18)',
        border: `1px solid ${ui.line}`,
        zIndex: 1500,
        fontFamily: ui.font,
        color: ui.ink,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '16px 18px 10px', display: 'flex', alignItems: 'flex-start', gap: 12, borderBottom: `1px solid ${ui.lineSoft}` }}>
        <h2 id={id} style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>
          {title}
        </h2>
        <CloseButton label={`Close ${title}`} onClick={onClose} />
      </div>
      {children}
    </div>
  );
};

const row = (label: string, value: React.ReactNode) => (
  <>
    <dt style={{ color: ui.muted }}>{label}</dt>
    <dd style={{ margin: 0, fontWeight: 500, wordBreak: 'break-word' }}>{value}</dd>
  </>
);

const HelpPanel: FC<{ onClose(): void }> = ({ onClose }) => {
  const org = useOrgSettings();
  const hasContact = org.helpEmail || org.helpPhone || org.helpName || org.helpHours;
  return (
    <Popover title="Need a hand?" id="help-title" onClose={onClose}>
      <div style={{ padding: '10px 18px 16px', fontSize: 14 }}>
        <p style={{ margin: '0 0 12px', color: ui.muted }}>{org.helpMessage}</p>
        {hasContact ? (
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '80px 1fr', gap: '8px 12px' }}>
            {org.helpName && row('Contact', org.helpName)}
            {org.helpEmail && row('Email', <a href={`mailto:${org.helpEmail}`}>{org.helpEmail}</a>)}
            {org.helpPhone && row('Phone', <a href={`tel:${org.helpPhone.replace(/\s+/g, '')}`}>{org.helpPhone}</a>)}
            {org.helpHours && row('Hours', org.helpHours)}
          </dl>
        ) : (
          <p style={{ margin: 0 }}>Your administrator hasn't added help contact details yet (Admin › Settings).</p>
        )}
        {org.helpEmail && (
          <div style={{ marginTop: 14 }}>
            <a href={`mailto:${org.helpEmail}?subject=${encodeURIComponent(`Help with ${org.databaseName}`)}`} style={{ ...button('primary'), width: '100%', justifyContent: 'center', boxSizing: 'border-box' }}>
              Email support
            </a>
          </div>
        )}
      </div>
      <div style={{ padding: '10px 18px', background: ui.field, borderTop: `1px solid ${ui.lineSoft}`, fontSize: 12, color: ui.muted }}>
        {org.companyName} · Archy {RELEASE} · {RELEASE_DATE}
      </div>
    </Popover>
  );
};

const minutesAgo = (iso?: string) => {
  if (!iso) return null;
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return m < 1 ? 'just now' : m === 1 ? '1 minute ago' : m < 120 ? `${m} minutes ago` : `${Math.round(m / 60)} hours ago`;
};

/** Only facts the app can actually see: no invented "all systems normal". */
const StatusPanel: FC<{ onClose(): void }> = ({ onClose }) => {
  const { role, user } = useAuth();
  const [search, setSearch] = useState<{ ok: boolean; text: string }>({ ok: false, text: 'Checking…' });
  useEffect(() => {
    firebase
      .firestore()
      .doc('_search/meta')
      .get()
      .then((d) => {
        const built = d.get('builtAt');
        const iso = built && built.toDate ? built.toDate().toISOString() : typeof built === 'string' ? built : undefined;
        setSearch(iso ? { ok: true, text: `Up to date as of ${minutesAgo(iso)}` } : { ok: false, text: 'Not built yet' });
      })
      .catch(() => setSearch({ ok: false, text: "Couldn't reach the search file" }));
  }, []);
  const dot = (ok: boolean) => (
    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: ok ? ui.complete : '#D97706', display: 'inline-block', marginRight: 8 }} />
  );
  return (
    <Popover title="System status" id="status-title" onClose={onClose}>
      <dl style={{ margin: 0, padding: '12px 18px 16px', display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', fontSize: 14 }}>
        {row('Signed in', <>{dot(!!user)}{user ? `${user.email}${role ? ` (${role})` : ''}` : 'No'}</>)}
        {row('Database', <>{dot(true)}Connected</>)}
        {row('Search', <>{dot(search.ok)}{search.text}</>)}
        {row('Stored in', REGION)}
        {row('Backups', 'Daily (kept 30 days) and weekly (kept 14 weeks); any change can be undone for 7 days')}
      </dl>
      <div style={{ padding: '12px 18px 16px', borderTop: `1px solid ${ui.lineSoft}` }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: ui.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>This version</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 20, fontWeight: 700 }}>{RELEASE}</span>
          <span style={{ fontSize: 13, color: ui.muted }}>deployed {RELEASE_DATE}</span>
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 12, color: ui.muted }} title={`${COMMIT} · ${BUILT_AT}`}>
          Year, week of the year, then that week's release. Quote it to support.
        </p>
      </div>
    </Popover>
  );
};

const AppFooter: FC = () => {
  const org = useOrgSettings();
  const [open, setOpen] = useState<'help' | 'status' | null>(null);
  const notLive = ENVIRONMENT !== 'production';
  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, color: ui.link, cursor: 'pointer', textDecoration: 'underline' };
  return (
    <>
      {/* Keeps page content clear of the floating footer. */}
      <div aria-hidden="true" style={{ height: FOOTER_HEIGHT + 24 }} />
      <footer
        className="archy-footer"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 90,
          height: FOOTER_HEIGHT,
          boxSizing: 'border-box',
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(6px)',
          borderTop: `1px solid ${ui.line}`,
          padding: '0 16px 0 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          fontSize: 12,
          color: ui.muted,
          fontFamily: ui.font,
        }}
      >
        <span style={{ fontWeight: 600, color: ui.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
          {org.companyName} {org.databaseName}
        </span>
        <span className="archy-footer-mid" style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px 14px', justifyContent: 'center', alignItems: 'center', minWidth: 0 }}>
          <span>
            Powered by <strong style={{ color: ui.ink }}>Archy</strong>
          </span>
          <span title="Coming once the platform is complete">Privacy</span>
          <span title="Coming once the platform is complete">Terms</span>
          <button type="button" data-popover-toggle style={linkBtn} onClick={() => setOpen(open === 'status' ? null : 'status')}>
            System status
          </button>
          <span title={`${COMMIT} · built ${BUILT_AT}`}>
            {RELEASE} · {RELEASE_DATE}
          </span>
          {notLive && (
            <span style={{ fontWeight: 700, color: '#FFFFFF', background: ui.accent, borderRadius: 999, padding: '2px 8px' }} title="Not the live database">
              {ENVIRONMENT}
            </span>
          )}
        </span>
        <button
          type="button"
          className="archy-btn"
          data-popover-toggle
          aria-haspopup="dialog"
          aria-expanded={open === 'help'}
          onClick={() => setOpen(open === 'help' ? null : 'help')}
          style={{
            marginLeft: 'auto',
            flex: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            height: 40,
            padding: '0 16px 0 5px',
            borderRadius: 999,
            border: 'none',
            background: ui.ink,
            color: '#FFFFFF',
            font: 'inherit',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: '50%', background: ui.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700 }}>
            ?
          </span>
          Help
        </button>
      </footer>
      {open === 'help' && <HelpPanel onClose={() => setOpen(null)} />}
      {open === 'status' && <StatusPanel onClose={() => setOpen(null)} />}
    </>
  );
};

export default AppFooter;
